/**
 * **Vendor…**, a Needs you block's pane (ADR 0194 decision 6, amending ADR
 * 0132): work that wasn't a client's, but a supplier's. The owner: "vendor
 * controls for calls as well as email".
 *
 * - **One box per party:** each email's other party's domain ("Remember
 *   vendor.example as a vendor: never count its email") and each call's
 *   number ("Remember (555) 555-0144 as a vendor: never count its calls"),
 *   all ticked to start.
 * - **Done** (`POST /api/v1/blocks/{id}/vendor`) remembers those ticked, so
 *   their email and calls stop counting, for everyone, and dismisses the
 *   block as a vendor's. Nothing ticked dismisses just the block. The popup
 *   then closes, and the next card has focus.
 * - **A refusal** names what it's about (a client's number, Axon's own
 *   domain): it's said under that box.
 *
 * Only for Needs you: a Ready block was matched to a client, so it's
 * dismissed instead (the API refuses it too).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import { useSession } from "../../app/session-context";
import { formatPhone } from "../../lib/phones";
import { type BlockView, vendorParties } from "./model";

const secondary =
  "h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return error.detail ?? "This block can't change any more. Reload to see where it is now.";
    }
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    return error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function VendorPane({
  block,
  onDone,
  onCancel,
}: {
  block: BlockView;
  /** Done, once the day shows it, with what happened (#247). */
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before. */
  onCancel: () => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const ids = useId();
  const { domains, numbers } = vendorParties(block);
  const parties = [
    ...domains.map((value) => ({ field: "domains" as const, value, shown: value, what: "email" })),
    ...numbers.map((value) => ({ field: "numbers" as const, value, shown: formatPhone(value), what: "calls" })),
  ];
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const ticked = (value: string) => !unticked.has(value);
  const done = useMutation({
    mutationFn: () =>
      api.markVendor(
        block.id,
        domains.filter(ticked),
        numbers.filter(ticked),
      ),
    onSuccess: async (marked) => {
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      const remembered = [...marked.domains, ...marked.numbers.map(formatPhone)];
      onDone(
        remembered.length === 0
          ? `Dismissed ${block.title}.`
          : `Dismissed ${block.title}, a vendor's. ${remembered.join(", ")} won't be counted again.`,
      );
    },
  });
  // A 422 names its box: "numbers", 0 is the first number sent.
  const named = done.error instanceof ApiError && done.error.status === 422 ? done.error.fieldErrors : [];
  const sentOf = (field: "domains" | "numbers") => (field === "domains" ? domains : numbers).filter(ticked);
  const errorFor = (field: "domains" | "numbers", value: string): string | null => {
    const at = sentOf(field).indexOf(value);
    return named.find((e) => e.field === field && e.index === at)?.message ?? null;
  };
  const unplaced = done.isError && !parties.some((p) => errorFor(p.field, p.value) !== null);

  return (
    <form
      aria-label="Vendor"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        done.mutate();
      }}
    >
      <p className="text-[13px] leading-[1.45] text-muted">
        Not client work. Ticked, it&apos;s remembered as a vendor and never counted again, for anyone.
        Unticked, just this block is dismissed.
      </p>
      {parties.map((party, index) => {
        const error = errorFor(party.field, party.value);
        const errorId = `${ids}-${String(index)}`;
        return (
          <div key={`${party.field}:${party.value}`} className="flex flex-col gap-1">
            <label className="flex items-start gap-2 text-[13.5px] leading-[1.35]">
              <input
                type="checkbox"
                checked={ticked(party.value)}
                aria-invalid={error !== null}
                aria-describedby={error === null ? undefined : errorId}
                onChange={(event) => {
                  const next = new Set(unticked);
                  if (event.target.checked) {
                    next.delete(party.value);
                  } else {
                    next.add(party.value);
                  }
                  setUnticked(next);
                }}
                className="mt-0.5 size-4 shrink-0 accent-brand"
              />
              {`Remember ${party.shown} as a vendor: never count its ${party.what}`}
            </label>
            {error === null ? null : (
              <p id={errorId} role="alert" className="pl-6 text-[13px] text-status-needs-client-fg">
                {error}
              </p>
            )}
          </div>
        );
      })}
      {unplaced ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(done.error)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={done.isPending}
          className="h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {done.isPending ? "Saving…" : "Done"}
        </button>
        <button type="button" onClick={onCancel} className={secondary}>
          Cancel
        </button>
      </div>
    </form>
  );
}
